import React, { FC, PropsWithChildren, ReactNode, useEffect, useId, useState } from "react";
import { Box, Card, Collapse, Flex, Text, UnstyledButton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronDown } from "@tabler/icons-react";
import styles from "@/PageComponents/Job/job-details.module.css";

export const SECTION_CONTENT_MAX_WIDTH = 1100;

/* One section of the job overview: its own card with a title, an optional "· count" and header actions.
   Collapsible sections start closed and open from their title. On a phone every other section turns into an accordion too,
   starting closed (the content stays mounted, so counts and add buttons keep working); alwaysOpenOnMobile opts one out. */
const JobSectionCard: FC<PropsWithChildren<{
    title: string;
    count?: ReactNode;
    headerRight?: ReactNode;
    // collapsible only: controls placed just after the title, shown while the section is open
    titleAside?: ReactNode;
    collapsible?: boolean;
    alwaysOpenOnMobile?: boolean;
    // opens the section on a phone (e.g. when something inside it needs attention)
    revealOnMobile?: boolean;
    initiallyOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    hidden?: boolean;
    id?: string;
    // a second column beside the section's content (stacks under it on narrow screens), with its own heading
    aside?: { title: string; content: ReactNode };
}>> = ({ title, count, headerRight, titleAside, collapsible = false, alwaysOpenOnMobile = false, revealOnMobile = false, initiallyOpen = false, onOpenChange, hidden = false, id, aside, children }) => {

    const isMobile = useMediaQuery('(max-width: 768px)') ?? false;
    const mobileAccordion = !collapsible && isMobile && !alwaysOpenOnMobile;

    const [open, setOpen] = useState(!collapsible || initiallyOpen);
    const [mobileOpen, setMobileOpen] = useState(false);
    useEffect(() => {
        if (revealOnMobile) setMobileOpen(true);
    }, [revealOnMobile]);

    const accordion = collapsible || mobileAccordion;
    const isOpen = mobileAccordion ? mobileOpen : open;
    const toggle = () => {
        if (mobileAccordion) {
            setMobileOpen(!mobileOpen);
            return;
        }
        setOpen(!open);
        onOpenChange?.(!open);
    };
    // the card is a region named by its title (without the count), so it can be found by name
    const titleId = useId();

    const heading = <Text component={'h2'} m={0} fz={15} fw={600} c={'gray.9'}>
        <span id={titleId}>{title}</span>
        {count !== undefined && count !== null && count !== '' &&
            <Text component={'span'} inherit fw={500} c={'gray.6'}> · {count}</Text>}
    </Text>;

    return (
        <Card component={'section'} aria-labelledby={titleId} withBorder radius={'md'} className={styles.sectionCard} id={id} display={hidden ? 'none' : undefined}>
            {/* the card is full width; what is inside stays within one shared maximum width, so every section lines up */}
            <Box maw={SECTION_CONTENT_MAX_WIDTH}>
            {aside ?
                <div className={styles.asideGrid}>
                    <Flex align={'center'} gap={'md'} mih={32} style={{ gridArea: 'head' }}>{heading}{headerRight}</Flex>
                    <Box pt={'sm'} data-section-panel style={{ gridArea: 'body' }}>{children}</Box>
                    <Flex align={'center'} mih={32} className={styles.asideHead} style={{ gridArea: 'asideHead' }}>
                        <Text component={'h2'} m={0} fz={15} fw={600} c={'gray.9'}>{aside.title}</Text>
                    </Flex>
                    <Box pt={'sm'} style={{ gridArea: 'asideBody' }}>{aside.content}</Box>
                </div> : <>
            <Flex align={'center'} gap={'md'} mih={32}>
                {collapsible && titleAside ?
                    <>
                        <UnstyledButton onClick={toggle} aria-expanded={isOpen} aria-labelledby={titleId}>
                            {heading}
                        </UnstyledButton>
                        {isOpen && titleAside}
                        {/* the rest of the header also toggles; the title is the keyboard control */}
                        <Flex flex={1} justify={'flex-end'} align={'center'} mih={32} style={{ cursor: 'pointer' }} onClick={toggle}>
                            <IconChevronDown size={18} color={'var(--mantine-color-gray-6)'}
                                             style={{ transition: 'transform 150ms ease', transform: isOpen ? 'rotate(180deg)' : undefined }} />
                        </Flex>
                    </> :
                accordion ?
                    <UnstyledButton onClick={toggle} aria-expanded={isOpen} aria-labelledby={titleId} style={{ flex: 1 }}>
                        <Flex align={'center'} gap={'md'}>
                            {heading}
                            <IconChevronDown size={18} color={'var(--mantine-color-gray-6)'}
                                             style={{ marginLeft: 'auto', transition: 'transform 150ms ease', transform: isOpen ? 'rotate(180deg)' : undefined }} />
                        </Flex>
                    </UnstyledButton> :
                    heading
                }
                {(!mobileAccordion || isOpen) && headerRight}
            </Flex>
            {accordion ?
                <Collapse in={isOpen}>
                    <Box pt={'sm'} data-section-panel>{collapsible ? isOpen && children : children}</Box>
                </Collapse> :
                <Box pt={'sm'} data-section-panel>{children}</Box>
            }
            </>}
            </Box>
        </Card>
    );
};

export default JobSectionCard;
